export type BadgeFields = {
  raw: string;
  externalId?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phone?: string;
  title?: string;
  company?: string;
  website?: string;
  address?: string;
  city?: string;
  state?: string;
  postal?: string;
  country?: string;
};

const EMAIL_RE = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i;
const URL_RE = /https?:\/\/[^\s]+/i;

function cleanValue(value: string): string | undefined {
  const normalized = value
    .replace(/\\n/g, "\n")
    .replace(/\\,/g, ",")
    .replace(/\\;/g, ";")
    .trim();
  return normalized || undefined;
}

function assignField(fields: BadgeFields, key: string, rawValue: string): void {
  const value = cleanValue(rawValue);
  if (!value) return;
  const normalizedKey = key.trim().toLowerCase().replace(/[\s_-]+/g, "");

  if (["firstname", "givenname", "fname"].includes(normalizedKey)) {
    fields.firstName = value;
    return;
  }
  if (["lastname", "surname", "familyname", "lname"].includes(normalizedKey)) {
    fields.lastName = value;
    return;
  }
  if (["name", "fullname", "fn"].includes(normalizedKey)) {
    fields.name = value;
    return;
  }
  if (["email", "emailaddress", "e-mail"].includes(normalizedKey)) {
    fields.email = value;
    return;
  }
  if (["phone", "mobile", "telephone", "tel", "workphone", "cellphone"].includes(normalizedKey)) {
    fields.phone = value;
    return;
  }
  if (["title", "jobtitle", "position"].includes(normalizedKey)) {
    fields.title = value;
    return;
  }
  if (["company", "organization", "org", "employer"].includes(normalizedKey)) {
    fields.company = value;
    return;
  }
  if (["website", "url", "web", "site"].includes(normalizedKey)) {
    fields.website = value;
    return;
  }
  if (["address", "address1", "street", "streetaddress"].includes(normalizedKey)) {
    fields.address = value;
    return;
  }
  if (normalizedKey === "city") {
    fields.city = value;
    return;
  }
  if (["state", "stateprovince", "province", "region"].includes(normalizedKey)) {
    fields.state = value;
    return;
  }
  if (["postal", "postalcode", "zip", "zipcode"].includes(normalizedKey)) {
    fields.postal = value;
    return;
  }
  if (["country", "countrycode"].includes(normalizedKey)) {
    fields.country = value;
    return;
  }
  if (["externalid", "id", "badgeid", "leadid"].includes(normalizedKey)) {
    fields.externalId = value;
  }
}

function hydrateName(fields: BadgeFields): void {
  if (fields.firstName && fields.lastName && !fields.name) {
    fields.name = `${fields.firstName} ${fields.lastName}`.trim();
  }
  if (fields.name && (!fields.firstName || !fields.lastName)) {
    const parts = fields.name.trim().split(/\s+/);
    if (parts.length >= 2) {
      fields.firstName = fields.firstName || parts.slice(0, -1).join(" ");
      fields.lastName = fields.lastName || parts[parts.length - 1];
    }
  }
}

export function parseBadgePayload(raw: string): BadgeFields {
  const text = raw.trim();
  const fields: BadgeFields = { raw: text };
  if (/BEGIN:VCARD/i.test(text)) {
    const fn = text.match(/^FN[:;].*$/im)?.[0]?.replace(/^FN[:;]/i, "").trim();
    const n = text.match(/^N[:;].*$/im)?.[0]?.replace(/^N[:;]/i, "").trim();
    const email = text.match(/^EMAIL[^:]*:.*$/im)?.[0]?.replace(/^EMAIL[^:]*:/i, "").trim();
    const phone = text.match(/^TEL[^:]*:.*$/im)?.[0]?.replace(/^TEL[^:]*:/i, "").trim();
    const title = text.match(/^TITLE[:;].*$/im)?.[0]?.replace(/^TITLE[:;]/i, "").trim();
    const company = text.match(/^ORG[:;].*$/im)?.[0]?.replace(/^ORG[:;]/i, "").trim();
    const website = text.match(/^URL[:;].*$/im)?.[0]?.replace(/^URL[:;]/i, "").trim();
    const adr = text.match(/^ADR[^:]*:.*$/im)?.[0]?.replace(/^ADR[^:]*:/i, "").trim();
    if (fn) fields.name = fn;
    else if (n) {
      const parts = n.split(";");
      fields.firstName = parts[1]?.trim() || undefined;
      fields.lastName = parts[0]?.trim() || undefined;
      fields.name = [fields.firstName, fields.lastName].filter(Boolean).join(" ").trim() || undefined;
    }
    if (email) fields.email = email;
    if (phone) fields.phone = phone;
    if (title) fields.title = title;
    if (company) fields.company = company;
    if (website) fields.website = website;
    if (adr) {
      const parts = adr.split(";");
      fields.address = [parts[2], parts[1], parts[0]].filter(Boolean).join(" ").trim() || undefined;
      fields.city = parts[3]?.trim() || undefined;
      fields.state = parts[4]?.trim() || undefined;
      fields.postal = parts[5]?.trim() || undefined;
      fields.country = parts[6]?.trim() || undefined;
    }
    hydrateName(fields);
    return fields;
  }
  if (/^mailto:/i.test(text)) {
    fields.email = text.replace(/^mailto:/i, "").split("?")[0].trim() || undefined;
    return fields;
  }
  try {
    const json = JSON.parse(text) as Record<string, unknown>;
    if (json && typeof json === "object" && !Array.isArray(json)) {
      for (const [key, value] of Object.entries(json)) {
        if (typeof value === "string") {
          assignField(fields, key, value);
        }
      }
      hydrateName(fields);
      return fields;
    }
  } catch {
    // Not JSON.
  }
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    const pair = line.match(/^\s*([^:]+)\s*:\s*(.+)\s*$/);
    if (!pair) continue;
    assignField(fields, pair[1], pair[2]);
  }
  hydrateName(fields);
  const email = text.match(EMAIL_RE)?.[0];
  if (email && !fields.email) fields.email = email;
  const website = text.match(URL_RE)?.[0];
  if (website && !fields.website) fields.website = website;
  if (!fields.name && text.length <= 80 && !/^https?:/i.test(text)) {
    fields.name = text;
    hydrateName(fields);
  }
  return fields;
}

export function badgeSearchQuery(fields: BadgeFields): string {
  return (fields.email || fields.name || fields.raw).trim();
}
